'use client';

import React, { useState, useMemo, useEffect } from 'react';
import DirectoryFilters from './DirectoryFilters';
import ProviderGrid from './ProviderGrid';
import DirectoryHeader from './DirectoryHeader';
import type { FilterState, Provider } from './types';
import { createClient } from '@/lib/supabase/client';

const DEFAULT_FILTERS: FilterState = {
  query: '',
  postcode: '',
  radius: 10,
  category: 'all',
  minRating: 0,
  tier: 'all',
  availableNow: false,
  sortBy: 'relevance',
};

// ── Normalise a scraped_listing row into a Provider ──────────────────────────
function normaliseListing(row: Record<string, unknown>): Provider {
  const postcode = (row.postcode as string) ?? '';
  const district = postcode.split(' ')[0] ?? postcode;
  const address = (row.address as string) ?? '';
  const city = address.split(',').pop()?.trim() ?? district;
  const location = city ? `${city}, ${district}` : district;

  return {
    id: row.id as string,
    slug: (row.id as string),
    name: (row.business_name as string) ?? 'Unknown Business',
    trade: (row.category as string) ?? 'Tradesperson',
    categorySlug: ((row.category as string) ?? '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    location,
    postcode,
    distanceMiles: 0,
    rating: row.rating != null ? Number(row.rating) : 0,
    reviewCount: 0,
    jobsDone: 0,
    tier: 'free',
    responseTime: 'Contact for availability',
    tagline: address || `${row.category ?? 'Tradesperson'} based in ${district}`,
    avatar: (row.photo_url as string) ?? '',
    avatarAlt: `${row.business_name ?? 'Business'} profile photo`,
    verified: false,
    availableNow: false,
    specialisms: [],
    joinedDate: (row.created_at as string) ?? new Date().toISOString(),
    completionRate: 0,
    lat: 0,
    lng: 0,
    source: 'scraped',
  };
}

// ── Normalise a provider_profiles row into a Provider ───────────────────────
function normaliseProfile(row: Record<string, unknown>): Provider {
  const postcode = (row.postcode as string) ?? '';
  const district = postcode.split(' ')[0] ?? postcode;
  const address = (row.address as string) ?? '';
  const city = address.split(',').pop()?.trim() ?? district;
  const location = city ? `${city}, ${district}` : district;

  const tier = (['free', 'pro', 'premium'].includes(row.subscription_tier as string)
    ? row.subscription_tier
    : 'free') as Provider['tier'];

  const services = Array.isArray(row.services) ? (row.services as string[]) : [];

  return {
    id: row.id as string,
    slug: (row.id as string),
    name: (row.business_name as string) ?? 'Unknown Business',
    trade: (row.trade_category as string) ?? 'Tradesperson',
    categorySlug: ((row.trade_category as string) ?? '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    location,
    postcode,
    distanceMiles: 0,
    rating: 0,
    reviewCount: 0,
    jobsDone: 0,
    tier,
    responseTime: 'Contact for availability',
    tagline: (row.bio as string) || `${row.trade_category ?? 'Tradesperson'} based in ${district}`,
    avatar: (row.photo_url as string) ?? '',
    avatarAlt: `${row.business_name ?? 'Business'} profile photo`,
    verified: true,
    availableNow: false,
    specialisms: services.slice(0, 6),
    joinedDate: (row.created_at as string) ?? new Date().toISOString(),
    completionRate: (row.completion_pct as number) ?? 0,
    lat: 0,
    lng: 0,
    source: 'claimed',
  };
}

export default function DirectoryShell() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const PER_PAGE = 9;

  // ── Fetch from Supabase ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function fetchProviders() {
      setLoading(true);
      setError(null);
      const supabase = createClient();

      try {
        // 1. Fetch claimed providers from provider_profiles
        const { data: profiles, error: profilesError } = await supabase
          .from('provider_profiles')
          .select('id, business_name, trade_category, postcode, address, bio, photo_url, services, subscription_tier, completion_pct, is_featured, created_at')
          .order('is_featured', { ascending: false })
          .order('subscription_tier', { ascending: false })
          .limit(500);

        if (profilesError) throw profilesError;

        // 2. Collect listing IDs that are already claimed so we don't double-show
        const claimedListingIds = new Set<string>();
        const { data: claimedRows } = await supabase
          .from('scraped_listings')
          .select('id')
          .eq('is_claimed', true);
        (claimedRows ?? []).forEach((r: { id: string }) => claimedListingIds.add(r.id));

        // 3. Fetch unclaimed scraped listings
        const { data: listings, error: listingsError } = await supabase
          .from('scraped_listings')
          .select('id, business_name, category, postcode, address, phone, website, rating, photo_url, is_claimed, created_at')
          .eq('is_claimed', false)
          .order('rating', { ascending: false, nullsFirst: false })
          .limit(1000);

        if (listingsError) throw listingsError;

        if (cancelled) return;

        const claimedProviders = (profiles ?? []).map((r) => normaliseProfile(r as Record<string, unknown>));
        const unclaimedProviders = (listings ?? []).map((r) => normaliseListing(r as Record<string, unknown>));

        // Claimed providers first, then unclaimed
        setProviders([...claimedProviders, ...unclaimedProviders]);
      } catch (err) {
        if (!cancelled) {
          console.error('Directory fetch error:', err);
          setError('Failed to load providers. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProviders();
    return () => { cancelled = true; };
  }, []);

  // ── Client-side filtering ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...providers];

    if (filters.query) {
      const q = filters.query.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.trade.toLowerCase().includes(q) ||
          p.specialisms.some((s) => s.toLowerCase().includes(q)) ||
          p.postcode.toLowerCase().includes(q)
      );
    }

    if (filters.postcode) {
      const pc = filters.postcode.toUpperCase().replace(/\s+/g, '');
      result = result.filter((p) =>
        p.postcode.toUpperCase().replace(/\s+/g, '').startsWith(pc.slice(0, 3))
      );
    }

    if (filters.category !== 'all') {
      result = result.filter(
        (p) =>
          p.categorySlug === filters.category ||
          p.trade.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === filters.category
      );
    }

    if (filters.minRating > 0) {
      result = result.filter((p) => p.rating >= filters.minRating);
    }

    if (filters.tier !== 'all') {
      result = result.filter((p) => p.tier === filters.tier);
    }

    if (filters.availableNow) {
      result = result.filter((p) => p.availableNow);
    }

    if (filters.sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (filters.sortBy === 'distance') {
      result.sort((a, b) => a.distanceMiles - b.distanceMiles);
    } else if (filters.sortBy === 'newest') {
      result.sort((a, b) => new Date(b.joinedDate).getTime() - new Date(a.joinedDate).getTime());
    } else {
      // relevance: claimed (verified) first, then by rating
      result.sort((a, b) => {
        if (a.source === 'claimed' && b.source !== 'claimed') return -1;
        if (b.source === 'claimed' && a.source !== 'claimed') return 1;
        return b.rating - a.rating;
      });
    }

    return result;
  }, [providers, filters]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
      <DirectoryHeader
        total={filtered.length}
        viewMode={viewMode}
        setViewMode={setViewMode}
        filters={filters}
        updateFilter={updateFilter}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
      />

      <div className="flex gap-6 xl:gap-8 mt-6">
        {/* Sidebar filters — desktop */}
        <aside className="hidden lg:block w-64 xl:w-72 flex-shrink-0">
          <DirectoryFilters filters={filters} updateFilter={updateFilter} />
        </aside>

        {/* Mobile filters drawer */}
        {filtersOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setFiltersOpen(false)}
            />
            <div className="relative ml-auto w-80 max-w-full bg-white h-full overflow-y-auto shadow-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-700 text-gray-900">Filters</h3>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  ✕
                </button>
              </div>
              <DirectoryFilters filters={filters} updateFilter={updateFilter} />
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-5">
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="bg-white border border-gray-100 rounded-2xl p-5 animate-pulse"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-14 h-14 rounded-xl bg-gray-100 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-3/4" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                      <div className="h-3 bg-gray-100 rounded w-2/3" />
                    </div>
                  </div>
                  <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-5/6 mb-4" />
                  <div className="flex gap-1.5 mb-4">
                    <div className="h-5 bg-gray-100 rounded-full w-16" />
                    <div className="h-5 bg-gray-100 rounded-full w-20" />
                    <div className="h-5 bg-gray-100 rounded-full w-14" />
                  </div>
                  <div className="h-px bg-gray-50 mb-3" />
                  <div className="flex justify-between">
                    <div className="h-3 bg-gray-100 rounded w-16" />
                    <div className="h-3 bg-gray-100 rounded w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="font-display font-700 text-gray-800 text-lg mb-2">Could not load providers</h3>
              <p className="text-gray-500 text-sm max-w-xs">{error}</p>
            </div>
          ) : (
            <ProviderGrid
              providers={paginated}
              total={filtered.length}
              page={page}
              totalPages={totalPages}
              perPage={PER_PAGE}
              setPage={setPage}
              viewMode={viewMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}